import { computed, toValue } from 'vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

/**
 * MY1-3079 — the auto-tracking vocabulary for a benefit, derived from the
 * `auto_tracking` object every benefit payload carries (BenefitAutoTrackingPresenter).
 * One mapping from state → label / tooltip / action, shared by the table
 * row, the mobile card and the details sidebar so the three never drift.
 */
export const AUTO_TRACKING_STATES = Object.freeze({
    AUTO_TRACKED: 'auto_tracked',
    AVAILABLE: 'available',
    SPENDING_DISABLED: 'spending_disabled',
    PREMIUM_REQUIRED: 'premium_required',
    MANUAL_ONLY: 'manual_only',
});

export const AUTO_TRACKED_LABEL = 'Auto-tracked';
export const AVAILABLE_LABEL = 'Auto-tracking available';

export const AVAILABLE_TOOLTIP =
    'Auto-tracks by connecting to your bank, click to connect';
export const SPENDING_DISABLED_TOOLTIP =
    'Enable My Spending to track benefit usage automatically';
export const SPENDING_DISABLED_ACTION_TOOLTIP =
    'Enable My Spending to track benefit usage automatically, click to turn it on';
export const PREMIUM_REQUIRED_TOOLTIP =
    'Auto-tracking is included with 10xTravel+, click to upgrade';

const STATIC_TOOLTIPS = Object.freeze({
    [AUTO_TRACKING_STATES.AVAILABLE]: AVAILABLE_TOOLTIP,
    [AUTO_TRACKING_STATES.SPENDING_DISABLED]: SPENDING_DISABLED_TOOLTIP,
    [AUTO_TRACKING_STATES.PREMIUM_REQUIRED]: PREMIUM_REQUIRED_TOOLTIP,
});

/**
 * "Synced 2 hours ago from your Chase bank account" — the design's copy for
 * the Auto-tracked label. Falls back gracefully when the matcher has not
 * written anything yet or the issuer name is unknown.
 */
export function autoTrackedTooltip(autoTracking, now = dayjs()) {
    const bank = autoTracking?.institution_name;
    const matchedAt = autoTracking?.last_matched_at
        ? dayjs(autoTracking.last_matched_at)
        : null;

    if (matchedAt && matchedAt.isValid()) {
        const when = matchedAt.from(now);

        return bank
            ? `Synced ${when} from your ${bank} bank account`
            : `Synced ${when} from My Spending`;
    }

    return bank
        ? `Tracks automatically from your ${bank} bank account`
        : 'Tracks automatically from My Spending';
}

/**
 * Tooltip for the Data Link under a transaction-backed usage record.
 */
export function usageRecordDataLinkTooltip(entry) {
    return entry?.source === 'auto'
        ? 'Tracked automatically from My Spending, click to view details'
        : 'Linked from My Spending, click to view details';
}

/**
 * Whether a usage record came from a My Spending transaction (either the
 * matcher's doing or the user's) and therefore has a transaction to link to.
 */
export function isTransactionBacked(entry) {
    return (
        !!entry?.mx_transaction_guid &&
        (entry?.source === 'auto' || entry?.source === 'user_linked')
    );
}

export function spendingTransactionUrl(entry) {
    return `/spending?transaction=${encodeURIComponent(entry.mx_transaction_guid)}`;
}

/**
 * @param {import('vue').MaybeRefOrGetter<object|null>} autoTracking
 * @param {{ canEnableSpending?: import('vue').MaybeRefOrGetter<boolean> }} [options]
 *   canEnableSpending — the viewer owns the card, so the My Spending opt-in
 *   behind `spending_disabled` may be offered (MY1-3083). Off by default: on
 *   another member's card the endpoint would opt the viewer in and leave the
 *   card untouched (MY1-3077).
 */
export function useBenefitAutoTracking(autoTracking, options = {}) {
    const data = computed(() => toValue(autoTracking) ?? null);
    const canEnableSpending = computed(
        () => toValue(options.canEnableSpending) === true
    );
    const state = computed(() => data.value?.state ?? null);

    const isAutoTracked = computed(
        () => state.value === AUTO_TRACKING_STATES.AUTO_TRACKED
    );
    const isAvailable = computed(
        () => state.value === AUTO_TRACKING_STATES.AVAILABLE
    );
    const isSpendingDisabled = computed(
        () => state.value === AUTO_TRACKING_STATES.SPENDING_DISABLED
    );
    const isPremiumRequired = computed(
        () => state.value === AUTO_TRACKING_STATES.PREMIUM_REQUIRED
    );

    // Manual-only cards (issuer cannot be connected) show nothing: there is
    // no action the user could take, so a label would only raise a question.
    const showLabel = computed(
        () =>
            isAutoTracked.value ||
            isAvailable.value ||
            isSpendingDisabled.value ||
            isPremiumRequired.value
    );

    const label = computed(() => {
        if (isAutoTracked.value) {
            return AUTO_TRACKED_LABEL;
        }

        return showLabel.value ? AVAILABLE_LABEL : '';
    });

    const tooltip = computed(() => {
        if (isAutoTracked.value) {
            return autoTrackedTooltip(data.value);
        }

        if (isSpendingDisabled.value && canEnableSpending.value) {
            return SPENDING_DISABLED_ACTION_TOOLTIP;
        }

        return STATIC_TOOLTIPS[state.value] ?? '';
    });

    // What clicking the label does. `spending_disabled` acts only on the
    // viewer's own card (see canEnableSpending); elsewhere it explains itself.
    const action = computed(() => {
        if (isAvailable.value) {
            return 'connect';
        }

        if (isPremiumRequired.value) {
            return 'upgrade';
        }

        if (isSpendingDisabled.value && canEnableSpending.value) {
            return 'enable-spending';
        }

        return null;
    });

    const isClickable = computed(() => action.value !== null);

    return {
        state,
        isAutoTracked,
        isAvailable,
        isSpendingDisabled,
        isPremiumRequired,
        showLabel,
        label,
        tooltip,
        action,
        isClickable,
    };
}
